import { EventService } from 'src/app/service/event.service';
import { Events, WebsocketService } from 'src/app/service/websocket.service';
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { tap, finalize } from 'rxjs/operators';
import { GeneralSettings } from 'src/app/model/Setting';
import { GeneralService } from 'src/app/service/general.service';
import { Utils } from 'src/app/utils';
import { Modal } from '../controls/modal/modal.service';
import { ModalMessageComponent } from '../controls/modal-message/modal-message.component';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { UserInfo } from 'src/app/model/User';
import { Subscription } from 'rxjs';
import { ModalConfig } from '../controls/modal/modal-config';
import { helpURLValidator } from 'src/app/validator/help-url-form-validator';

@Component({
    selector: 'app-settings-general',
    templateUrl: './settings-general.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-general.component.css'],
    standalone: false
})
export class SettingsGeneralComponent implements OnInit {

  isLoading:boolean = true;
  isSaving:boolean = false;
  settingsForm = new FormGroup({
    checkOnlineModeCtrl: new FormControl(),
    sessionTimeoutCtrl: new FormControl('', [
      Validators.required,
      Validators.min(1),
      Validators.max(60),
      Validators.pattern("^[0-9]*$")
    ]),
    helpURLCtrl: new FormControl('', [
      Validators.required,
      Validators.maxLength(2048),
      helpURLValidator()
    ]),
  });
  savingBtn:string = $localize`Save`;
  currentUserAuthInfo : UserInfo;
  settings:GeneralSettings;
  eventMessageSubscription: Subscription;

  constructor(private genService: GeneralService,
              private wsService: WebsocketService,
              private eventService: EventService,
              private modal: Modal,
              private authService: AuthenticationService,) { 
                this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
                  if(msg.eventType == Events.SettingsConnectionMode){
                    this.initData();
                  }
                });
              }

  get checkOnlineModeCtrl() { return this.settingsForm.get('checkOnlineModeCtrl'); }
  get sessionTimeoutCtrl() { return this.settingsForm.get('sessionTimeoutCtrl'); }
  get helpURLCtrl() { return this.settingsForm.get('helpURLCtrl');}
  
  ngOnInit(): void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.initData();
  }

  ngOnDestroy() {
    this.eventMessageSubscription.unsubscribe();
  }

  initData(showLoading:boolean = true): void{
    if(showLoading){
      this.isLoading = true;
    }
    this.genService.getGeneralSettings().pipe(tap(resp => {
      this.settings = resp.body;
      this.checkOnlineModeCtrl.setValue(this.settings.isOnlineMode);
      this.sessionTimeoutCtrl.setValue(this.settings.sessionTimeout);
      this.helpURLCtrl.setValue(this.settings.helpURL);
      this.settingsForm.reset(this.settingsForm.value);
    }),
    finalize(() => {
      this.isLoading = false;
    }),
    ).subscribe();
  }

  save(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Save`,
          message: $localize`The changes made on this page will be saved. Do you want to save?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-general-save'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.isSaving = true;
          this.savingBtn = $localize`Saving...`;
          this.settings.isOnlineMode = this.checkOnlineModeCtrl.value;
          this.settings.sessionTimeout = this.sessionTimeoutCtrl.value;
          this.settings.helpURL = this.helpURLCtrl.value.trim();

          this.genService.updateGeneralSettings(this.settings, this.wsService.from).pipe(tap(resp => {
            if (resp.body) {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were saved successfully`);
              this.currentUserAuthInfo.settings.isOnlineMode = this.settings.isOnlineMode;
              this.authService.updateLoggedInUser(this.currentUserAuthInfo);
              this.eventService.broadcastHelpURL(this.settings.helpURL);
              this.initData(false);
            } else {
              this.settingsForm.reset(this.settingsForm.value);
              Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Settings could not be saved. Please try again later.`);
            }
          }),
            finalize(() => {
              this.isSaving = false;
              this.savingBtn = $localize`Save`;
            }),
          ).subscribe();
        }
        sub.unsubscribe();
      });
  }

  reset(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Reset`,
          message: $localize`The changes made on this page will be lost. Do you want to reset?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-general-reset'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.initData();
        }
        sub.unsubscribe();
      });
  }

  canDeactivate() : boolean{
    return !this.settingsForm.dirty;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-general-unsaved-changes');
  }
}
